<template>
  <aside class="key-performance-indicators-page">
    <DocumentPageTitle
      :word-count="totalWords"
      :word-limit="wordLimit"
      title="Performance Monitoring Framework"
    />

    <div>
      <template
        v-for="storeItem in storeItems"
        :key="storeItem.id"
      >
        <TextEditorInput
          :id="storeItem.id"
          v-model="planContentKey[storeItem.storeKey].text"
          :label="storeItem.label"
          :word-limit="wordLimit"
          :total-word-count="totalWords"
          @update:word-count="handleWordCountUpdate"
        />

        <DocumentAnnexureUploadButton
          :store-key="`keyPerformanceIndicators.${storeItem.storeKey}.documents`"
        />
      </template>

      <div class="flex items-center justify-between mt-12 mb-6">
        <h2 class="!my-0 !text-black">
          Performance targets
        </h2>

        <AppButton
          class="btn-outline btn-secondary !p-2 rounded !font-normal"
          @click="showBatchImport = true"
        >
          <FontAwesomeIcon :icon="faUpload" />
          Batch upload
        </AppButton>
      </div>
    </div>

    <template v-if="planStore.content.developmentStrategies.length > 0">
      <div
        v-for="developmentStrategy in planStore.content.developmentStrategies"
        :key="developmentStrategy.id"
        class="bg-editor-strategies-bg border-y border-chrome-medium !py-10 !px-0 mb-10 transition-none"
        :class="getIndicators(developmentStrategy.id).length > 0 ? '!pb-0' : ''"
      >
        <div class="px-20">
          <h2 class="!text-black">
            {{ developmentStrategy.name }}
          </h2>

          <div
            v-if="getIndicators(developmentStrategy.id).length === 0"
            class="light-chrome-banner mb-4"
          >
            <strong>Please Note:</strong> Each Development Strategy needs corresponding indicators. Please Click "+ Add a new indicator" button below to get started.
          </div>

          <AppButton
            class="!p-2 btn-dashed btn-outline btn-secondary-filled"
            @click="formModal.open('performanceTarget', performanceTargetScheme, { strategyId: developmentStrategy.id })"
          >
            <FontAwesomeIcon :icon="faPlus" />
            Add a new indicator
          </AppButton>
        </div>

        <div
          v-for="performanceTarget in getIndicators(developmentStrategy.id)"
          :key="performanceTarget.id"
          class="py-8 border-b border-dashed last:border-b-0 border-editor-strategies-divider"
        >
          <div class="pl-20 pr-5">
            <div class="flex items-center justify-between w-full mb-3">
              <p class="text-base font-medium !mb-0">
                {{ performanceTarget.name }}
              </p>

              <DocumentActions
                subject="indicator"
                @edit-clicked="formModal.open('performanceTarget', performanceTarget, { strategyId: developmentStrategy.id })"
                @delete-clicked="deleteModal.open('performanceTarget', performanceTarget, { strategyId: developmentStrategy.id })"
              />
            </div>

            <p
              class="text-base mb-8"
              v-html="domPurify.sanitize(performanceTarget.description) || 'No description provided'"
            />
          </div>

          <div class="px-4">
            <AppTable>
              <AppTableHeader>
                <th class="!text-right">
                  Baseline
                </th>

                <template
                  v-for="i in 5"
                  :key="i"
                >
                  <th class="!text-right">
                    <span class="block">Year {{ i }}</span>
                    <span>target</span>
                  </th>

                  <th
                    v-if="i < 5"
                    class="!text-right"
                  >
                    <span class="block">Year {{ i }}</span>
                    <span>actual</span>
                  </th>
                </template>
              </AppTableHeader>

              <AppTableBody>
                <tr class="[&_td]:align-top [&_td]:!text-right">
                  <td>{{ formatThousands(performanceTarget.indicatorValues?.baseline) }}</td>

                  <template
                    v-for="i in 5"
                    :key="i"
                  >
                    <td>
                      {{ formatThousands(performanceTarget.indicatorValues?.[`year${i}`]?.target) }}
                    </td>

                    <td v-if="i < 5">
                      {{ formatThousands(performanceTarget.indicatorValues?.[`year${i}`]?.actual) }}
                    </td>
                  </template>
                </tr>
              </AppTableBody>
            </AppTable>
          </div>
        </div>
      </div>
    </template>

    <div v-else>
      <div class="light-chrome-banner mb-4">
        <strong>Please Note:</strong> that in order to start entering indicators, you have to add development strategies.
      </div>
    </div>

    <AppModal
      v-if="formModal.type === 'performanceTarget'"
      size="5xl"
      :title="formModal.isEditing ? 'Edit Indicator' : 'Add New Indicator'"
      :disabled="formModal.processing"
      @close="handleCancel"
    >
      <template #body>
        <KpiStrategyForm
          v-model="formModal.data"
          :error="error"
          @submit="savePerformanceTarget"
        />
      </template>

      <template #footer>
        <ButtonSubmitRow
          :processing="formModal.processing"
          @submit-clicked="savePerformanceTarget"
          @cancel-clicked="handleCancel"
        />
      </template>
    </AppModal>

    <ConfirmationModal
      v-if="deleteModal.type === 'performanceTarget'"
      intent="danger"
      title="Delete Indicator"
      :processing="deleteModal.processing"
      @close="deleteModal.close"
      @confirm="deletePerformanceTarget"
    >
      Are you sure you want to delete the indicator <strong>{{ deleteModal.data.name }}</strong>?
    </ConfirmationModal>

    <BatchImportModal
      v-if="showBatchImport"
      :definition="indicatorsImport"
      @close="showBatchImport = false"
    />
  </aside>
</template>

<script setup>
import { computed, ref } from 'vue';

import { AppButton, AppModal } from '@/components/app';
import { ButtonSubmitRow } from '@/components/buttons';
import { ConfirmationModal } from '@/components/modals';
import { DocumentActions, DocumentAnnexureUploadButton, DocumentPageTitle } from '@/components/plan-document/components';
import { TextEditorInput } from '@/components/form-inputs';
import { KpiStrategyForm } from '@/components/plan-document/forms';
import { BatchImportModal } from '@/components/plan-document/import';
import { AppTable, AppTableBody, AppTableHeader } from '@/components/tables';

import { faPlus, faUpload } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { useDomPurify, useModal, useStringFormatter, useTinyMCEWordCount } from '@/composables';
import { indicatorsImport } from '@/composables/import';
import { SECTION_COUNT_EXCLUDE, SECTION_WORD_LIMIT } from '@/composables/import/specs/indicators';
import { countWords } from '@/composables/import/wordCount';
import { usePlanStore } from '@/store';

const { formatThousands } = useStringFormatter();

const error = ref(null);
const planStore = usePlanStore();
const domPurify = useDomPurify();

const wordLimit = SECTION_WORD_LIMIT;

const planContentKey = computed(() => {
  return planStore.content?.keyPerformanceIndicators || {};
});

const storeItems = [
  { label: 'Introduction to performance indicators', storeKey: 'introduction', id: 'introduction' },
];

const storeItemIds = storeItems.map(s => s.id);

const { totalWords: editorWords, updateCount } = useTinyMCEWordCount(wordLimit, storeItemIds);

/**
 * TinyMCE reports live counts for the fields it owns, so those store keys are excluded from
 * the walk below to avoid counting them twice. Everything else in the section is derived from
 * store data — indicator descriptions today, and any prose field added later, matching the
 * exclude list the API applies to this same section.
 */
const countExclude = [...SECTION_COUNT_EXCLUDE, ...storeItems.map(s => s.storeKey)];

const totalWords = computed(() => {
  return editorWords.value + countWords(planContentKey.value, countExclude);
});

const handleWordCountUpdate = ({ id, count }) => {
  updateCount(id, count);
};

const formModal = useModal();
const deleteModal = useModal();

const showBatchImport = ref(false);

const performanceTargetScheme = {
  developmentStrategyId: null,
  name: null,
  description: null,
  indicatorValues: {
    baseline: null,
    year1: { target: null, actual: null },
    year2: { target: null, actual: null },
    year3: { target: null, actual: null },
    year4: { target: null, actual: null },
    year5: { target: null, actual: null },
  },
};

// Fixed: Logic to filter indicators by strategy ID
const getIndicators = (strategyId) => {
  const targets = planContentKey.value?.performanceTargets || [];
  return targets.filter(t => t.developmentStrategyId === strategyId);
};

const savePerformanceTarget = () => {
  formModal.processing = true;
  const { data, context, isEditing } = formModal;

  if (!data.name) {
    error.value = 'This is a required field';
    formModal.processing = false;
    return;
  }

  if (!data.developmentStrategyId) {
    data.developmentStrategyId = context.strategyId;
  }

  // Ensure path exists
  if (!planContentKey.value.performanceTargets) {
    planContentKey.value.performanceTargets = [];
  }

  if (isEditing) {
    const index = planContentKey.value.performanceTargets.findIndex(i => i.id === data.id);
    if (index !== -1) {
      planContentKey.value.performanceTargets[index] = { ...data };
    }
  } else {
    planContentKey.value.performanceTargets.push({ ...data, id: Date.now() });
  }

  formModal.close();
  error.value = null;
};

const deletePerformanceTarget = () => {
  deleteModal.processing = true;
  const { data } = deleteModal;

  if (planContentKey.value.performanceTargets) {
    planContentKey.value.performanceTargets = planContentKey.value.performanceTargets
      .filter(i => i.id !== data.id);
  }

  deleteModal.close();
};

const handleCancel = () => {
  error.value = null;
  formModal.close();
};

</script>
